import { tracked } from '@glimmer/tracking';
import { Day } from 'garaje/utils/enums';
import moment from 'moment-timezone';

const dateFormat = 'YYYYMMDDHHmmss';

const DAY_NAMES: Record<RecurringRuleWeekday, string> = {
  SU: 'Sunday',
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
};

const MONTH_NAMES = [
  '',
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const ORDINAL_NUMBERS: Record<string, string> = {
  '1': 'first',
  '2': 'second',
  '3': 'third',
  '4': 'fourth',
  '5': 'fifth',
  '-1': 'the last',
};

interface RecurringRuleOBJ {
  FREQ: string;
  BYDAY: string;
  BYMONTH: string;
  BYMONTHDAY: string;
  UNTIL: string;
}

type RecurringRuleWeekday = 'SU' | 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA';

export function byDayValue(day: Day): RecurringRuleWeekday {
  switch (day) {
    case Day.Sunday:
      return 'SU';
    case Day.Monday:
      return 'MO';
    case Day.Tuesday:
      return 'TU';
    case Day.Wednesday:
      return 'WE';
    case Day.Thursday:
      return 'TH';
    case Day.Friday:
      return 'FR';
    case Day.Saturday:
      return 'SA';
  }
}

export function dayFromRule(recurringRule: RecurringRule): Day {
  if (!recurringRule.byDay) {
    throw new Error(`Tried to retrieve the day value from a RecurringRule that does not have one`);
  }
  switch (recurringRule.byDay) {
    case 'SU':
      return Day.Sunday;
    case 'MO':
      return Day.Monday;
    case 'TU':
      return Day.Tuesday;
    case 'WE':
      return Day.Wednesday;
    case 'TH':
      return Day.Thursday;
    case 'FR':
      return Day.Friday;
    case 'SA':
      return Day.Saturday;
  }
}

export function formatUntil(date: Date): string {
  const formattedDate = moment.utc(moment(date).endOf('date').subtract(59, 'seconds')).format(dateFormat);
  return formattedDate + 'Z';
}

export default class RecurringRule {
  @tracked frequency: string | null = null;
  @tracked byDay: RecurringRuleWeekday | null = null;
  @tracked byMonth: string | null = null;
  @tracked byMonthDay: string | null = null;
  @tracked until: Date | null = null;

  constructor(args = {}) {
    Object.assign(this, args);
  }

  isBlank(): boolean {
    return this.toString() === '';
  }

  toString(): string {
    let string = '';

    if (this.frequency) {
      string = string + `FREQ=${this.frequency};`;
    }
    if (this.byDay) {
      string = string + `BYDAY=${this.byDay};`;
    }
    if (this.byMonth) {
      string = string + `BYMONTH=${this.byMonth};`;
    }
    if (this.byMonthDay) {
      string = string + `BYMONTHDAY=${this.byMonthDay};`;
    }
    if (this.until) {
      string = string + `UNTIL=${formatUntil(this.until)};`;
    }

    return string;
  }

  ruleToText(): string {
    if (this.isBlank()) {
      return 'Does not repeat';
    }

    switch (this.frequency) {
      case 'DAILY':
        return 'Daily';

      case 'WEEKLY':
        if ((this.byDay as string) === 'MO,TU,WE,TH,FR') {
          return 'Every weekday (Monday to Friday)';
        }
        if (this.byDay) {
          const day = DAY_NAMES[this.byDay];
          if (day) {
            return `Weekly on ${day}`;
          }
        }
        return 'Weekly';

      case 'MONTHLY':
        if (this.byDay) {
          const match = String(this.byDay).match(/^(-?\d+)([A-Z]{2})$/);
          if (match && match[1] && match[2]) {
            const ordinal = match[1];
            const dayCode = match[2] as RecurringRuleWeekday;
            const ordinalText = ORDINAL_NUMBERS[ordinal] || ordinal;
            const dayName = DAY_NAMES[dayCode];
            if (dayName) {
              return `Monthly on ${ordinalText} ${dayName}`;
            }
          }
        }
        return 'Monthly';

      case 'YEARLY':
        if (this.byMonth && this.byMonthDay) {
          const monthName = MONTH_NAMES[parseInt(this.byMonth)];
          if (monthName) {
            return `Annually on ${monthName} ${this.byMonthDay}`;
          }
        }
        return 'Annually';

      default:
        return 'Recurring';
    }
  }

  untilToText(): string {
    if (!this.until) {
      return '';
    }
    return moment(this.until).format('MMM D, YYYY');
  }

  toText(): string {
    if (this.until) {
      return `${this.ruleToText()} until ${this.untilToText()}`;
    }
    return this.ruleToText();
  }

  static parse(rruleStr: string): RecurringRule {
    const rruleObj = (rruleStr || '').split(';').reduce<Partial<RecurringRuleOBJ>>((acc, elem) => {
      const arr = <Array<keyof RecurringRuleOBJ>>elem.split('=');
      acc[arr[0]!] = arr[1];
      return acc;
    }, {});

    return new RecurringRule({
      frequency: rruleObj['FREQ'],
      byDay: <RecurringRuleWeekday>rruleObj['BYDAY'],
      byMonth: rruleObj['BYMONTH'],
      byMonthDay: rruleObj['BYMONTHDAY'],
      until: rruleObj['UNTIL'] ? moment(rruleObj['UNTIL'], dateFormat + 'Z') : null,
    });
  }
}
